const BASE = process.env.REACT_APP_API_URL || '/api';
const REQUEST_TIMEOUT_MS = 30000;

const getToken = () => localStorage.getItem('rb_token');

const req = async (method, path, body) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('Request timed out. Please try again.');
    throw e;
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 401) {
    localStorage.removeItem('rb_token');
    window.dispatchEvent(new Event('auth:logout'));
  }

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : {};
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};

export const api = {
  requestSignupCode: (name, email, password) => req('POST', '/auth/signup/request-code', { name, email, password }),
  signup: (name, email, password, code, referralCode) => req('POST', '/auth/signup', { name, email, password, code, referralCode }),
  login: (email, password) => req('POST', '/auth/login', { email, password }),
  requestLoginCode: (email, password) => req('POST', '/auth/login/request-code', { email, password }),
  verifyLogin: (email, code) => req('POST', '/auth/login/verify', { email, code }),
  googleAuth: (credential, referralCode) => req('POST', '/auth/google', { credential, referralCode }),
  linkedinAuth: (code, redirectUri, referralCode) => req('POST', '/auth/linkedin', { code, redirectUri, referralCode }),

  // Referral system
  getReferralCode:   () => req('GET', '/referral/code'),
  getReferralStats:  () => req('GET', '/referral/stats'),
  redeemPoints:      () => req('POST', '/referral/redeem'),
  validateRefCode:   (code) => req('GET', `/referral/validate/${code}`),

  // Admin referral management
  getAdminReferralStats:  () => req('GET', '/admin/referrals/stats'),
  getAdminReferrals:      (params = '') => req('GET', `/admin/referrals${params}`),
  getAdminUserReferrals:  (userId) => req('GET', `/admin/referrals/user/${userId}`),
  adjustUserPoints:       (userId, points, reason) => req('POST', `/admin/referrals/user/${userId}/adjust`, { points, reason }),
  flagReferral:           (id, fraudFlag) => req('PUT', `/admin/referrals/${id}/flag`, { fraudFlag }),
  getMe: () => req('GET', '/auth/me'),
  updateProfile: (profile) => req('PUT', '/auth/profile', { profile }),
  changePassword: ({ currentPassword, newPassword }) => req('PUT', '/auth/password', { currentPassword, newPassword }),
  deleteAccount: () => req('DELETE', '/auth/me'),
  completeOnboarding: () => req('POST', '/auth/onboarding-complete'),

  getDocs: () => req('GET', '/documents'),
  getDoc: (id) => req('GET', `/documents/${id}`),
  createDoc: (data) => req('POST', '/documents', data),
  updateDoc: (id, data) => req('PUT', `/documents/${id}`, data),
  deleteDoc: (id) => req('DELETE', `/documents/${id}`),
  getAnnouncements: () => req('GET', '/announcements'),

  getInterviews: () => req('GET', '/interviews'),
  getInterview: (id) => req('GET', `/interviews/${id}`),
  createInterview: (data) => req('POST', '/interviews', data),
  updateInterview: (id, data) => req('PUT', `/interviews/${id}`, data),
  deleteInterview: (id) => req('DELETE', `/interviews/${id}`),
  emailInterviewReport: (id, email) => req('POST', `/interviews/${id}/email-report`, { email }),

  getSectionConfig: () => fetch(`${BASE}/config/section-config`).then(r => r.json()),
  getPlatformFlags: () => fetch(`${BASE}/config/platform-flags`).then(r => r.json()),

  // Premium: AI Section Analysis (strengths + issues per resume section)
  analyzeSection: (payload) => req('POST', '/ai/analyze-section', payload),

  // Stripe billing
  createCheckoutSession: (billingCycle) => req('POST', '/payment/checkout', { billingCycle }),
  createPortalSession:   () => req('POST', '/payment/portal'),
};
